import { toast } from "react-toastify";
import moment from "moment";
import axios from "axios";
// import { generateTask } from "../../features/tasks/taskSlice";
// import { getMeeting } from "../../features/meetings/meetingsSlice";

export const handleIconSource = (platformType, ICONS) => {
  return ICONS[platformType] || ICONS.other || null;
};

export const truncateText = (text, limit = 250) =>
  text?.length > limit ? `${text.substring(0, limit)}...` : text;

export const formatDate = (date) => {
  return moment.utc(date || "").format("MMM D, YYYY • h:mm A");
};

const prepareTaskInput = (tasks, taskData) => {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return [];
  }

  if (!Array.isArray(taskData?.actions)) {
    return [];
  }

  const matchedTasks = taskData?.actions?.filter((task) =>
    tasks.includes(task.uuid),
  );

  return matchedTasks;
};

export const canExpandTask = (task) => {
  return task.status !== "completed";
};

export const getTaskTooltip = (task, action = "selection") => {
  if (task.status === "completed") {
    const messages = {
      selection: "Task completed — selection disabled",
      details: "Task completed — details disabled",
      sending: "Task completed — sending disabled",
    };
    return messages[action];
  }
  return undefined;
};

export const initializePageData = (dispatch, meetingId, actions) => {
  dispatch(actions.getUserActivity());
  dispatch(actions.getMeeting(meetingId));
  dispatch(actions.getTask(meetingId));
};

export const createMeetingHandlers = ({
  dispatch,
  meeting,
  task,
  updateTask,
  setSelectedTasks,
  setUpdatingTaskId,
  setShowAddModal,
  setShowSummaryModal,
  setShowTranscriptModal,
  setShowParticipantsModal,
  setShowRegenerateModal,
  selectedTasks,
  executeTask,
  setExecutingTaskIds,
}) => {
  const handleSend = async (tasks, meetingId) => {
    const matchedActions = prepareTaskInput(tasks, task);

    const allIds = matchedActions.map((a) => a.uuid);

    setExecutingTaskIds((prev) => {
      const updated = new Set(prev);
      allIds.forEach((id) => updated.add(id));
      return updated;
    });

    setSelectedTasks([]);

    for (const action of matchedActions) {
      try {
        const { success } = await dispatch(
          executeTask({
            taskData: action,
            meetingId,
          }),
        ).unwrap();
        if (success) {
          toast.success(`${truncateText(action.name, 50)} completed`);
        } else {
          toast.error(`${truncateText(action.name, 50)} failed`);
        }
      } catch (err) {
        toast.error(
          `${truncateText(action.name, 50)} failed : ${err?.message || ""}`,
        );
      } finally {
        setExecutingTaskIds((prev) => {
          const updated = new Set(prev);
          updated.delete(action.uuid);
          return updated;
        });
      }
    }
  };
  const handleToggleTask = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  };

  const handleAddTask = () => {
    setShowAddModal(true);
  };

  const handleRegenerate = async (prompt, meeting) => {
    try {
      // const { uuid: meetingId, agent } = meeting;
      // const { uuid: agentId } = agent;
      // await dispatch(generateTask({ prompt, meetingId, agentId })).unwrap();
      // await dispatch(getMeeting(meetingId));
      toast.success("regenerated task is processing");
    } catch (error) {
      console.error("Failed to regenerate:", error);
      toast.error("Failed to regenerate task");
    }
  };

  const handleShowTranscript = () => {
    setShowTranscriptModal(true);
  };

  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  const handleShowParticipants = () => {
    setShowParticipantsModal(true);
  };

  const handleShowRegenerate = () => {
    setShowRegenerateModal(true);
  };

  const handleHideAddModal = () => {
    setShowAddModal(false);
  };

  const handleHideTranscriptModal = () => {
    setShowTranscriptModal(false);
  };

  const handleHideSummaryModal = () => {
    setShowSummaryModal(false);
  };

  const handleHideRegenerateModal = () => {
    setShowRegenerateModal(false);
  };

  const handleHideParticipantsModal = () => {
    setShowParticipantsModal(false);
  };

  const handleToggleAll = () => {
    const selectableTasks = task?.actions || [];
    const allSelectableSelected = selectableTasks.every((t) =>
      selectedTasks.includes(t.uuid),
    );

    if (allSelectableSelected) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(selectableTasks.map((t) => t.uuid));
    }
  };

  const handleTogglePlatformTask = (platformTasks) => {
    const allSelected = platformTasks.every((t) =>
      selectedTasks.includes(t.uuid),
    );

    if (allSelected) {
      setSelectedTasks((prev) =>
        prev.filter((id) => !platformTasks.some((t) => t.uuid === id)),
      );
    } else {
      setSelectedTasks((prev) => [
        ...prev,
        ...platformTasks.map((t) => t.uuid).filter((id) => !prev.includes(id)),
      ]);
    }
  };

  return {
    handleSend,
    handleAddTask,
    handleRegenerate,
    handleShowSummary,
    handleShowTranscript,
    handleShowRegenerate,
    handleShowParticipants,
    handleHideAddModal,
    handleHideTranscriptModal,
    handleHideSummaryModal,
    handleHideParticipantsModal,
    handleHideRegenerateModal,
    handleToggleAll,
    handleToggleTask,
    handleTogglePlatformTask,
  };
};

//File-type detection
const EXTENSION_MAP = {
  mp3: "recordings",
  m4a: "recordings",
  wav: "recordings",
  ogg: "recordings",
  aac: "recordings",
  flac: "recordings",

  png: "images",
  jpg: "images",
  jpeg: "images",
  webp: "images",
  gif: "images",
  svg: "images",
  bmp: "images",

  pdf: "documents",
  doc: "documents",
  docx: "documents",
  ppt: "documents",
  pptx: "documents",
  xls: "documents",
  xlsx: "documents",
  txt: "documents",
  csv: "documents",
  md: "documents",
};

const MIME_MAP = {
  "audio/mpeg": "recordings",
  "audio/mp4": "recordings",
  "audio/wav": "recordings",
  "audio/ogg": "recordings",
  "audio/aac": "recordings",
  "audio/flac": "recordings",
  "audio/x-wav": "recordings",

  "image/png": "documents",
  "image/jpeg": "documents",
  "image/webp": "documents",
  "image/gif": "documents",
  "image/svg+xml": "documents",
  "image/bmp": "documents",

  "application/pdf": "documents",
  "application/msword": "documents",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "documents",
  "application/vnd.ms-powerpoint": "documents",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "documents",
  "application/vnd.ms-excel": "documents",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "documents",
  "text/plain": "documents",
  "text/csv": "documents",
  "text/markdown": "documents",
};

export function detectFileType(file) {
  const byMime = MIME_MAP[file.type];
  if (byMime) return byMime;

  const ext = file.name.split(".").pop()?.toLowerCase();
  return EXTENSION_MAP[ext] ?? "documents";
}

export const DROPZONE_ACCEPT = {
  "audio/mpeg": [".mp3"],
  "audio/mp4": [".m4a"],
  "audio/wav": [".wav"],

  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
  "image/svg+xml": [".svg"],

  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],
  "text/plain": [".txt"],
};

export const FILE_TYPE_LABELS = {
  recordings: "Audio",
  documents: "Documents",
};

export const STATUS_COLOR = {
  pending: "secondary",
  uploading: "primary",
  done: "success",
  error: "danger",
  removing: "warning",
};

export const STATUS_LABEL = {
  pending: "Pending",
  uploading: "Uploading",
  done: "Uploaded",
  error: "Error",
  removing: "Removing…",
};

export function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function slugify(name) {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.-]/g, "");
}

export async function uploadToS3(uploadUrl, file, onProgress) {
  await axios.put(uploadUrl, file, {
    headers: {},
    onUploadProgress: (e) => {
      if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    transformRequest: [(data) => data],
  });
}

export const isAudioFile = (file) => {
  const name = file?.name || "";
  const mimeType = file?.type || "";
  return mimeType.startsWith("audio/") || /\.(mp3|m4a|wav)$/i.test(name);
};
export const getStatusBadge = (status) => {
  switch (status) {
    case "COMPLETED":
      return "badge border border-success text-success bg-transparent";
    case "PROCESSING":
    case "PENDING":
    case "PROCESSING_TASKS":
      return "badge border border-warning text-warning bg-transparent";
    case "FAILED":
      return "badge border border-danger text-danger bg-transparent";
    default:
      return "badge border border-secondary text-secondary bg-transparent";
  }
};

export const AVATAR_COLORS = [
  "#e04a39",
  "#00b894",
  "#0984e3",
  "#6c5ce7",
  "#e17055",
  "#00cec9",
  "#fd79a8",
  "#ffb224",
];

export const getInitials = (name = "") => {
  const digits = name.replace(/[^0-9]/g, "");
  if (digits.length >= 2 && /^[+\d\s\-().]+$/.test(name.trim())) {
    return digits.slice(0, 2);
  }

  const cleanName = name.replace(/^[^a-zA-Z0-9]+/, "");
  if (!cleanName) return "?";
  return cleanName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const getParticipantSecondary = (p) => {
  return p.username
    ? (p.username.includes("@") ? p.username : p.platform)
    : p.platform;
};

export const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};